import { c as _c } from "react/compiler-runtime";
import { Button, IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { Type } from '@repo/design-system/components/ui/type';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import type React from 'react';
import { ExploreActionMenu } from './explore-action-menu';
import type { ExploreAction } from './explore-actions';
interface ExploreActionSheetProps {
  actions: ExploreAction[];
  imageUrl: string | null;
  isAskLoading: boolean;
  isCompactViewport: boolean;
  isMobilePane: boolean;
  selectedActionId: string | null;
  title: string;
  onAskSubmit: (query: string) => void;
  onBack: () => void;
  onClose: () => void;
  onSelect: (action: ExploreAction) => void;
}
const panelStyles = css({
  position: 'relative',
  width: 'full',
  overflow: 'hidden',
  borderRadius: '32',
  backgroundColor: 'blackAlpha.90',
  backdropFilter: '[blur(16px)]',
  flexShrink: '0'
});
const imageBandStyles = css({
  position: 'relative',
  height: '[72px]',
  marginBottom: '[-24px]',
  width: 'full',
  overflow: 'hidden',
  flexShrink: '0'
});
const imageStyles = css({
  position: 'absolute',
  top: '[50%]',
  left: '0',
  width: 'full',
  height: '[140px]',
  objectFit: 'cover',
  transform: '[translateY(-50%)]'
});
const imageFallbackStyles = css({
  position: 'absolute',
  inset: '0',
  backgroundColor: 'whiteAlpha.10'
});
const imageOverlayStyles = css({
  position: 'absolute',
  inset: '0',
  background: '[linear-gradient(90deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.4) 100%), linear-gradient(180deg, rgba(0, 0, 0, 0) 69.063%, rgba(0, 0, 0, 0.5) 100%)]'
});
const closeButtonStyles = css.raw({
  position: 'absolute',
  top: '[20px]',
  right: '[20px]',
  zIndex: 2,
  boxShadow: '[0px 1px 0px 0px rgba(0,0,0,0.3)]'
});
const mobileCloseButtonStyles = css.raw({
  top: '[16px]',
  right: '[16px]'
});
const contentStyles = css.raw({
  position: 'relative',
  zIndex: 1,
  paddingX: '20',
  paddingBottom: '20'
});
const compactContentStyles = css.raw({
  paddingX: '12'
});
const mobileContentStyles = css.raw({
  paddingX: '16'
});
const searchIconStyles = css({
  width: '[48px]',
  height: '[48px]',
  minWidth: '[48px]',
  minHeight: '[48px]',
  borderRadius: 'full',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'white',
  color: 'black',
  backdropFilter: '[blur(24px)]'
});
const eyebrowStyles = css({
  letterSpacing: '[0.75px]',
  textTransform: 'uppercase'
});
const titleStyles = css({
  textAlign: 'center',
  wordBreak: 'break-word',
  maxWidth: 'full'
});
const contextStyles = css({
  textAlign: 'center',
  maxWidth: '[284px]'
});
const backButtonStyles = css.raw({
  backdropFilter: '[blur(8px)]'
});
export const ExploreActionSheet: React.FC<ExploreActionSheetProps> = t0 => {
  const $ = _c(61);
  const {
    actions,
    imageUrl,
    isAskLoading,
    isCompactViewport,
    isMobilePane,
    selectedActionId,
    title,
    onAskSubmit,
    onBack,
    onClose,
    onSelect
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["game_map"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useT(t1);
  let t2;
  if ($[1] !== imageUrl) {
    t2 = imageUrl ? <img src={imageUrl} alt="" className={imageStyles} /> : <div className={imageFallbackStyles} />;
    $[1] = imageUrl;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className={imageOverlayStyles} />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t(_temp);
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isMobilePane) {
    t5 = css({
      ...closeButtonStyles,
      ...(isMobilePane ? mobileCloseButtonStyles : {})
    });
    $[6] = isMobilePane;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon icon="close" decorative={true} />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== onClose || $[10] !== t4 || $[11] !== t5) {
    t7 = <IconButton onClick={onClose} color="primary" size="sm" aria-label={t4} className={t5}>{t6}</IconButton>;
    $[9] = onClose;
    $[10] = t4;
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t2 || $[14] !== t7) {
    t8 = <div className={imageBandStyles}>{t2}{t3}{t7}</div>;
    $[13] = t2;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== isCompactViewport) {
    t9 = isCompactViewport ? compactContentStyles : {};
    $[16] = isCompactViewport;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== isMobilePane) {
    t10 = isMobilePane ? mobileContentStyles : {};
    $[18] = isMobilePane;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t9) {
    t11 = {
      ...contentStyles,
      ...t9,
      ...t10
    };
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className={searchIconStyles}><Icon icon="manage_search" size="lg" decorative={true} /></div>;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t) {
    t13 = t(_temp2);
    $[24] = t;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13) {
    t14 = <Type variant="caption" color="white" className={eyebrowStyles}>{t13}</Type>;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== title) {
    t15 = <Type variant="header-m" color="white" title={title} className={titleStyles}>{title}</Type>;
    $[28] = title;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t || $[31] !== title) {
    t16 = t(_temp3, {
      pinName: title
    });
    $[30] = t;
    $[31] = title;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t16) {
    t17 = <Type variant="body-s" color="whiteAlpha.70" className={contextStyles}>{t16}</Type>;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t14 || $[36] !== t15 || $[37] !== t17) {
    t18 = <FlexColumn align="center" gap="6" width="full">{t14}{t15}{t17}</FlexColumn>;
    $[35] = t14;
    $[36] = t15;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const t19 = isMobilePane ? "mobileSheet" : "desktopSheet";
  let t20;
  if ($[39] !== actions || $[40] !== isAskLoading || $[41] !== onAskSubmit || $[42] !== onSelect || $[43] !== selectedActionId || $[44] !== t19) {
    t20 = <ExploreActionMenu actions={actions} variant={t19} selectedActionId={selectedActionId} isAskLoading={isAskLoading} onSelect={onSelect} onAskSubmit={onAskSubmit} />;
    $[39] = actions;
    $[40] = isAskLoading;
    $[41] = onAskSubmit;
    $[42] = onSelect;
    $[43] = selectedActionId;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  let t22;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = css(backButtonStyles);
    t22 = <Icon icon="arrow_back" decorative={true} />;
    $[46] = t21;
    $[47] = t22;
  } else {
    t21 = $[46];
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t) {
    t23 = t(_temp4);
    $[48] = t;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== onBack || $[51] !== t23) {
    t24 = <Button onClick={onBack} color="secondary" size="lg" fullWidth={true} className={t21}>{t22}{t23}</Button>;
    $[50] = onBack;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t11 || $[54] !== t18 || $[55] !== t20 || $[56] !== t24) {
    t25 = <FlexColumn gap="8" align="center" width="full" className={t11}>{t12}{t18}{t20}{t24}</FlexColumn>;
    $[53] = t11;
    $[54] = t18;
    $[55] = t20;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t25 || $[59] !== t8) {
    t26 = <div className={panelStyles}>{t8}{t25}</div>;
    $[58] = t25;
    $[59] = t8;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  return t26;
};
function _temp($) {
  return $.close;
}
function _temp2($_0) {
  return $_0.explore_menu.eyebrow;
}
function _temp3($_1) {
  return $_1.explore_menu.context_for_pin;
}
function _temp4($_2) {
  return $_2.explore_menu.back_to_pin_details;
}